import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AddressDTO, API, CompanyFullDTO, LocationDTO, LocationFullDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@ngx-seatback/utils';
import { QueryParamService } from '../../../../services/lists/query-params';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-location-edit',
  templateUrl: './location-edit.component.html',
  styleUrls: ['./location-edit.component.scss'],
  standalone: false,
})
export class LocationEditComponent implements OnInit {
  location: LocationDTO;
  locationId: number;
  loading: boolean;
  mapLoading: boolean;
  wizardState: string;
  point: any;
  form: UntypedFormGroup;
  manager: CompanyFullDTO;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    private translate: TranslateService,
    private navService: NavigationService,
    private queryParamService: QueryParamService,
    private cdRef: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {
    this.manager = null;
    this.location = undefined;
    this.locationId = null;
    this.loading = true;
    this.mapLoading = false;
    this.wizardState = 'Map';
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      UniqueIdentifier: new UntypedFormControl(''),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get UniqueIdentifier() {
    return this.form.get('UniqueIdentifier');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.locationId = params.locationID;
      this.refreshFromServer();
    });

    this.registerFormChangesListener();
  }

  retrieveManagedBy() {
    const managerID = this.queryParamService.getQueryParam('managedBy');
    this.queryParamService.clearQueryParam('managedBy');
    if (managerID) {
      this.api
        .shared_Company_retrieveCompanyById(managerID)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((comp: CompanyFullDTO) => {
          this.manager = comp;
          if (comp) {
            this.selectCompany(comp);
          }
        });
    }
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.location.name = value;
    });
    this.UniqueIdentifier.valueChanges.subscribe(value => {
      this.location.identifier = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.location.name);
    this.Name.markAsTouched();
    this.UniqueIdentifier.setValue(this.location.identifier);
    this.UniqueIdentifier.markAsTouched();
    if (this.location?.address?.position?.coordinates) {
      this.point = {
        x: this.location?.address?.position?.coordinates[0],
        y: this.location?.address?.position?.coordinates[1],
        zoom: 16,
      };
    }
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.locationId) {
      this.location = new LocationFullDTO();
      this.location.address = new AddressDTO();
      this.location.address.country = 'DE';
      this.loading = false;
      this.retrieveManagedBy();
    } else {
      this.api
        .ss_fm_Location_retrieveById(this.locationId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (loc: LocationFullDTO) => {
            this.location = loc;
            this.updateForm();
            this.cdRef.detectChanges();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateLocationQuestion() {
    if (!this.location.address.position.coordinates[0] || !this.location.address.position.coordinates[1]) {
      this.translate.get(marker('lang.location.submitWithoutGeoCoordinates')).subscribe(title => {
        this.translate.get(marker('lang.location.submitWithoutGeoCoordinatesQuestion')).subscribe(message => {
          this.translate.get(marker('lang.globals.yes')).subscribe(yes => {
            this.translate.get(marker('lang.location.submitWithoutGeoCoordinates.no')).subscribe(no => {
              this.modal
                .confirm({
                  title,
                  message,
                  yes,
                  no,
                })
                .then((result: boolean) => {
                  if (result) {
                    this.updateLocation();
                  }
                })
                .catch(() => {});
            });
          });
        });
      });
    } else {
      this.updateLocation();
    }
  }

  private updateLocation() {
    this.loading = true;
    if (!this.location.id) {
      if (this.location?.managedBy?.id) {
        this.api
          .ss_fm_Location_createLocationManagedByCompany(this.location.managedBy.id, this.location as LocationFullDTO)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (locationData: LocationFullDTO) => {
              this.location = locationData;
              this.navService.navigateToPoint(this.util.RoutingID.locationDetails, [
                {
                  id: 'locationID',
                  value: this.location.id,
                },
              ]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ss_fm_Location_createLocation(this.location as LocationFullDTO)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (locationData: LocationFullDTO) => {
              this.location = locationData;
              this.navService.navigateToPoint(this.util.RoutingID.locationDetails, [
                {
                  id: 'locationID',
                  value: this.location.id,
                },
              ]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .ss_fm_Location_editLocation(this.location as LocationFullDTO)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (locationData: LocationDTO) => {
            this.location = locationData;
            this.navService.navigateToPoint(this.util.RoutingID.locationDetails, [
              {
                id: 'locationID',
                value: this.location.id,
              },
            ]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  selectCompany(company: CompanyFullDTO) {
    this.translate.get(marker('lang.location.copyAddressfromCompanyTitle')).subscribe(title => {
      this.translate.get(marker('lang.location.copyAddressfromCompanyQuestion')).subscribe(message => {
        this.translate.get(marker('lang.globals.yes')).subscribe(yes => {
          this.translate.get(marker('lang.location.copyAddressfromCompanyQuestion.no')).subscribe(no => {
            this.changeWizardState('Map');
            this.location.managedBy = company;
            this.modal
              .confirm({
                title,
                message,
                yes,
                no,
              })
              .then((result: boolean) => {
                if (result) {
                  this.loading = true;
                  this.location.name = company.name;
                  this.location.identifier = company.identifier;
                  this.location.address = { ...company.address };
                  this.updateForm();
                  this.cdRef.detectChanges();
                  this.loading = false;
                }
              })
              .catch(() => {});
          });
        });
      });
    });
  }

  sliceCompany() {
    this.location.managedBy = null;
  }

  createCompany(company: CompanyFullDTO) {
    this.changeWizardState('Map');
    this.location.managedBy = company;
  }
}
